/**
 * Groups Page - admin list of every group.
 *
 * Server-paged and server-sorted. Search and the visibility / official / archived filters are
 * applied by the API before paging (so totals are right) and live in the URL (?q=, ?visibility=,
 * ?official=, ?archived=). No type filter: the API's GroupType has a single value.
 */

import { useState, useEffect, useCallback } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  Box,
  Typography,
  Chip,
  Button,
  Switch,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material'
import { Refresh, Visibility } from '@mui/icons-material'
import { PageShell } from '@components/common/PageShell'
import { DataTable, type DataTableColumn } from '@components/common/DataTable'
import { SearchField } from '@components/common/SearchField'
import { ConfirmDialog } from '@components/common/ConfirmDialog'
import { socialApi } from '@api/endpoints/social'
import {
  AdminGroupListItem,
  AdminGroupSortField,
  GroupVisibility,
  GroupType,
  getGroupVisibilityName,
} from '@api/types/social'

function getGroupTypeName(type: GroupType): string {
  switch (type) {
    case GroupType.Master:
      return 'Master'
    case GroupType.Temporary:
      return 'Temporary'
    default:
      return 'Unknown'
  }
}

function getVisibilityColor(visibility: GroupVisibility): 'success' | 'warning' | 'info' | 'default' {
  switch (visibility) {
    case GroupVisibility.Public:
      return 'success'
    case GroupVisibility.InviteOnly:
      return 'warning'
    case GroupVisibility.MatchingTagRequired:
      return 'info'
    default:
      return 'default'
  }
}

const formatDate = (dateStr?: string) => {
  if (!dateStr) return 'N/A'
  const date = new Date(dateStr)
  return isNaN(date.getTime()) ? 'N/A' : date.toLocaleDateString()
}

/** Tri-state URL value ('true' / 'false' / absent) to an optional boolean filter. */
const parseBool = (v: string | null): boolean | undefined =>
  v === 'true' ? true : v === 'false' ? false : undefined

const VISIBILITY_OPTIONS = [
  GroupVisibility.Public,
  GroupVisibility.MatchingTagRequired,
  GroupVisibility.InviteOnly,
]

const OFFICIAL_EXPLANATION =
  'Adventures from an official group get a live leaderboard without waiting for the minimum players and ratings, and players who know the creator (friends, group-mates) earn full points on them. Takes effect at the next leaderboard recompute.'

export function GroupsPage() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const search = searchParams.get('q') ?? ''
  const visibilityParam = searchParams.get('visibility')
  const visibility =
    visibilityParam !== null && VISIBILITY_OPTIONS.includes(Number(visibilityParam))
      ? (Number(visibilityParam) as GroupVisibility)
      : undefined
  const isOfficial = parseBool(searchParams.get('official'))
  const isArchived = parseBool(searchParams.get('archived'))
  const hasFilters = !!search || visibility !== undefined || isOfficial !== undefined || isArchived !== undefined

  const [groups, setGroups] = useState<AdminGroupListItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)

  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(25)
  const [totalCount, setTotalCount] = useState(0)

  // Default is newest first.
  const [sortBy, setSortBy] = useState<AdminGroupSortField>(AdminGroupSortField.CreatedDate)
  const [sortDescending, setSortDescending] = useState(true)

  const fetchGroups = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await socialApi.getAdminGroups({
        page: page + 1, // API is 1-based
        pageSize,
        sortBy,
        sortDescending,
        search: search || undefined,
        visibility,
        isOfficial,
        isArchived,
      })
      setGroups(response.items || [])
      setTotalCount(response.totalCount || 0)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch groups')
    } finally {
      setLoading(false)
    }
  }, [page, pageSize, sortBy, sortDescending, search, visibility, isOfficial, isArchived])

  useEffect(() => {
    fetchGroups()
  }, [fetchGroups])

  /** Set (or clear, with '') one URL filter and go back to the first page. */
  const setParam = (key: string, value: string) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        if (value) next.set(key, value)
        else next.delete(key)
        return next
      },
      { replace: true },
    )
    setPage(0)
  }

  const handleSearch = (q: string) => setParam('q', q)

  const handleSort = (key: string) => {
    const field = key as AdminGroupSortField
    if (sortBy === field) {
      setSortDescending(!sortDescending)
    } else {
      setSortBy(field)
      setSortDescending(false)
    }
    setPage(0)
  }

  // Official changes leaderboard scoring for every adventure the group owns, so the switch only
  // asks; the change happens after the confirm.
  const [pendingOfficial, setPendingOfficial] = useState<{ group: AdminGroupListItem; isOfficial: boolean } | null>(null)
  const [savingOfficial, setSavingOfficial] = useState(false)
  const confirmOfficialChange = async () => {
    if (!pendingOfficial) return
    const { group, isOfficial } = pendingOfficial
    setSavingOfficial(true)
    setActionError(null)
    try {
      await socialApi.setGroupOfficial(group.id, isOfficial)
      setGroups((current) => current.map((g) => (g.id === group.id ? { ...g, isOfficial } : g)))
      setPendingOfficial(null)
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to update the group')
      setPendingOfficial(null)
    } finally {
      setSavingOfficial(false)
    }
  }

  const openGroup = (group: AdminGroupListItem) => navigate(`/groups/${group.id}`)

  const officialSwitch = (group: AdminGroupListItem) => (
    <Box component="span" onClick={(e) => e.stopPropagation()}>
      <Switch
        size="small"
        checked={group.isOfficial}
        onChange={(e) => setPendingOfficial({ group, isOfficial: e.target.checked })}
        inputProps={{ 'aria-label': `Official: ${group.name}` }}
      />
    </Box>
  )

  const nameCell = (group: AdminGroupListItem) => (
    <Box sx={{ minWidth: 0, opacity: group.isArchived ? 0.6 : 1 }}>
      <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
        <Typography variant="body2" fontWeight="bold" sx={{ overflowWrap: 'anywhere' }}>
          {group.name}
        </Typography>
        {group.isOfficial && <Chip label="Official" size="small" color="primary" />}
        {group.isArchived && <Chip label="Archived" size="small" />}
      </Stack>
      {group.description && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 250 }}
        >
          {group.description}
        </Typography>
      )}
    </Box>
  )

  const columns: DataTableColumn<AdminGroupListItem>[] = [
    { key: 'name', header: 'Name', primary: true, render: nameCell },
    {
      key: 'creator',
      header: 'Creator',
      hideBelow: 'md',
      render: (g) => <Typography variant="body2">{g.creatorDisplayName}</Typography>,
    },
    {
      key: 'visibility',
      header: 'Visibility',
      render: (g) => (
        <Chip
          label={getGroupVisibilityName(g.visibility)}
          size="small"
          color={getVisibilityColor(g.visibility)}
          variant="outlined"
        />
      ),
    },
    { key: 'type', header: 'Type', hideBelow: 'lg', render: (g) => getGroupTypeName(g.groupType) },
    { key: 'official', header: 'Official', align: 'center', render: officialSwitch },
    {
      key: 'members',
      header: 'Members',
      align: 'right',
      sortKey: AdminGroupSortField.MemberCount,
      render: (g) => g.memberCount,
    },
    {
      key: 'discussions',
      header: 'Discussions',
      align: 'right',
      hideBelow: 'lg',
      sortKey: AdminGroupSortField.DiscussionCount,
      render: (g) => g.discussionCount,
    },
    { key: 'adventures', header: 'Adventures', align: 'right', hideBelow: 'md', render: (g) => g.adventureCount },
    { key: 'events', header: 'Events', align: 'right', hideBelow: 'lg', render: (g) => g.eventCount },
    {
      key: 'pending',
      header: 'Pending',
      align: 'center',
      hideBelow: 'md',
      render: (g) =>
        g.pendingRequestCount > 0 ? (
          <Chip label={g.pendingRequestCount} size="small" color="warning" />
        ) : (
          <Typography variant="body2" color="text.secondary">-</Typography>
        ),
    },
    {
      key: 'created',
      header: 'Created',
      hideBelow: 'md',
      sortKey: AdminGroupSortField.CreatedDate,
      render: (g) => formatDate(g.createdDate),
    },
  ]

  // Two lines per group on a phone: name + chips, then the counts that matter, with the
  // Official switch labelled so it is not a mystery toggle.
  const renderCard = (g: AdminGroupListItem) => (
    <Box>
      {nameCell(g)}
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
        <Chip
          label={getGroupVisibilityName(g.visibility)}
          size="small"
          color={getVisibilityColor(g.visibility)}
          variant="outlined"
        />
        <Typography variant="caption" color="text.secondary">
          {g.memberCount} members · {g.adventureCount} adventures
          {g.pendingRequestCount > 0 ? ` · ${g.pendingRequestCount} pending` : ''}
        </Typography>
      </Stack>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 0.5 }}>
        <Typography variant="caption" color="text.secondary">
          by {g.creatorDisplayName} · {formatDate(g.createdDate)}
        </Typography>
        <Stack direction="row" alignItems="center">
          <Typography variant="caption">Official</Typography>
          {officialSwitch(g)}
        </Stack>
      </Stack>
    </Box>
  )

  return (
    <PageShell
      title="Groups"
      subtitle={
        <>
          {totalCount > 0 ? `${totalCount} groups. ` : ''}
          Official groups skip the leaderboard popularity gate and give full points to the creator's friends.
        </>
      }
      actions={
        <Button variant="outlined" startIcon={<Refresh />} onClick={fetchGroups}>
          Refresh
        </Button>
      }
    >
      {actionError && (
        <Typography color="error" variant="body2" sx={{ mb: 1 }}>
          {actionError}
        </Typography>
      )}
      <DataTable
        rows={groups}
        columns={columns}
        getRowKey={(g) => g.id}
        loading={loading}
        error={error}
        onRetry={fetchGroups}
        emptyMessage={hasFilters ? 'No groups match these filters' : 'No groups yet'}
        page={page}
        pageSize={pageSize}
        total={totalCount}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        sortBy={sortBy}
        sortDescending={sortDescending}
        onSortChange={handleSort}
        onRowClick={openGroup}
        renderCard={renderCard}
        getRowActions={(g) => [{ key: 'view', label: 'View', icon: <Visibility />, onClick: () => openGroup(g) }]}
        toolbar={
          <>
            <SearchField value={search} onSearch={handleSearch} placeholder="Search groups" />
            <FormControl size="small" sx={{ minWidth: 150, flex: { xs: '1 1 140px', sm: '0 0 auto' } }}>
              <InputLabel>Visibility</InputLabel>
              <Select
                label="Visibility"
                value={visibility === undefined ? '' : String(visibility)}
                onChange={(e) => setParam('visibility', e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                {VISIBILITY_OPTIONS.map((v) => (
                  <MenuItem key={v} value={String(v)}>
                    {getGroupVisibilityName(v)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 130, flex: { xs: '1 1 120px', sm: '0 0 auto' } }}>
              <InputLabel>Official</InputLabel>
              <Select
                label="Official"
                value={isOfficial === undefined ? '' : String(isOfficial)}
                onChange={(e) => setParam('official', e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="true">Official</MenuItem>
                <MenuItem value="false">Not official</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 130, flex: { xs: '1 1 120px', sm: '0 0 auto' } }}>
              <InputLabel>Archived</InputLabel>
              <Select
                label="Archived"
                value={isArchived === undefined ? '' : String(isArchived)}
                onChange={(e) => setParam('archived', e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="false">Active</MenuItem>
                <MenuItem value="true">Archived</MenuItem>
              </Select>
            </FormControl>
          </>
        }
      />

      <ConfirmDialog
        open={pendingOfficial !== null}
        title={pendingOfficial?.isOfficial ? 'Mark group official?' : 'Remove official status?'}
        confirmLabel={pendingOfficial?.isOfficial ? 'Mark official' : 'Remove official'}
        destructive={pendingOfficial?.isOfficial === false}
        loading={savingOfficial}
        onConfirm={confirmOfficialChange}
        onClose={() => !savingOfficial && setPendingOfficial(null)}
      >
        {pendingOfficial && (
          <>
            <Typography variant="body2" sx={{ mb: 1, overflowWrap: 'anywhere' }}>
              <strong>{pendingOfficial.group.name}</strong>
              {pendingOfficial.isOfficial
                ? ' will become an official group.'
                : ' will go back to a normal group, and its adventures must again reach the minimum players and ratings before their leaderboards go live.'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {OFFICIAL_EXPLANATION}
            </Typography>
          </>
        )}
      </ConfirmDialog>
    </PageShell>
  )
}
